import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, ArrowRight, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import ContactFormModal from '../components/ContactFormModal';
import '../styles/animations.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DynamicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [currentButtonConfig, setCurrentButtonConfig] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dynamic-pages/${slug}`);
      setPage(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching page:', err);
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <FileText size={64} className="text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Page Not Found</h1>
          <p className="text-slate-600 mb-4">The page you're looking for doesn't exist.</p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Breadcrumb Schema for custom pages
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.skandaitsolutions.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": page?.title || "Page",
        "item": `https://www.skandaitsolutions.com/page/${slug}`
      }
    ]
  };

  return (
    <div data-testid={`dynamic-page-${slug}`} className="pt-20">
      <SEO 
        title={page.title}
        description={page.meta_description || page.hero_subtitle}
        keywords={page.meta_keywords}
        canonicalUrl={`https://www.skandaitsolutions.com/page/${page.slug}`}
      />
      
      {/* Structured Data for better indexing */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative py-24 overflow-hidden"
        style={{
          background: page.hero_image_url 
            ? `linear-gradient(rgba(15, 23, 42, ${(page.hero_overlay_opacity || 70) / 100}), rgba(15, 23, 42, ${(page.hero_overlay_opacity || 70) / 100})), url(${page.hero_image_url}) center/cover`
            : page.hero_bg_color
              ? `linear-gradient(135deg, ${page.hero_bg_color} 0%, ${page.hero_bg_color}dd 100%)`
              : 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" 
            style={{ 
              fontFamily: 'Manrope, sans-serif',
              color: page.hero_text_color || '#ffffff'
            }}
          >
            {page.hero_title}
          </h1>
          {page.hero_subtitle && (
            <p 
              className="text-xl max-w-3xl mx-auto"
              style={{ color: page.hero_text_color || '#e2e8f0' }}
            >
              {page.hero_subtitle}
            </p>
          )}
        </div>
      </section>

      {/* Content Sections */}
      {page.sections && page.sections.length > 0 && (
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {page.sections.map((section, index) => (
              <div key={index} className="mb-12">
                {section.type === 'text' && (
                  <div 
                    style={{ 
                      backgroundColor: section.bg_color || '#ffffff',
                      color: section.text_color || '#000000'
                    }} 
                    className="prose prose-lg max-w-none py-8 px-4"
                  >
                    {section.title && (
                      <h2 
                        className={`text-3xl mb-4 font-${section.font_weight || 'bold'} ${section.italic ? 'italic' : ''} ${section.underline ? 'underline' : ''}`}
                        style={{ 
                          fontFamily: 'Manrope, sans-serif', 
                          color: section.text_color || '#000000',
                          textAlign: section.text_align || 'left',
                          fontSize: section.font_size === '2xl' ? '2rem' : section.font_size === 'xl' ? '1.75rem' : section.font_size === 'lg' ? '1.5rem' : section.font_size === 'sm' ? '1rem' : '1.25rem'
                        }}
                      >
                        {section.title}
                      </h2>
                    )}
                    <div 
                      className={`whitespace-pre-line font-${section.font_weight || 'normal'} text-${section.font_size || 'base'} ${section.italic ? 'italic' : ''} ${section.underline ? 'underline' : ''}`}
                      style={{ 
                        color: section.text_color || '#000000',
                        textAlign: section.text_align || 'left'
                      }}
                    >
                      {section.content}
                    </div>
                  </div>
                )}

                {section.type === 'image' && (
                  <div className="text-center">
                    {section.image_url ? (
                      <img 
                        src={section.image_url} 
                        alt={section.alt || section.title || 'Image'} 
                        className="rounded-lg shadow-lg mx-auto max-w-full"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const errorDiv = document.createElement('div');
                          errorDiv.className = 'bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-8 mx-auto max-w-2xl';
                          errorDiv.innerHTML = `
                            <div class="text-slate-400 mb-2">
                              <svg class="w-16 h-16 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                              </svg>
                            </div>
                            <p class="text-slate-600 font-semibold mb-1">Image failed to load</p>
                            <p class="text-xs text-slate-500 break-all">URL: ${section.image_url}</p>
                            <p class="text-xs text-slate-400 mt-2">Please check if the URL is correct and accessible</p>
                          `;
                          e.target.parentNode.insertBefore(errorDiv, e.target);
                        }}
                      />
                    ) : (
                      <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-8 mx-auto max-w-2xl">
                        <div className="text-slate-400 mb-2">
                          <svg className="w-16 h-16 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                        </div>
                        <p className="text-slate-600 font-semibold">No image URL provided</p>
                        <p className="text-xs text-slate-400 mt-2">Please add an image URL in the admin panel</p>
                      </div>
                    )}
                    {section.caption && (
                      <p className="text-sm text-slate-500 mt-2">{section.caption}</p>
                    )}
                  </div>
                )}

                {section.type === 'cards' && section.items && (
                  <div>
                    {section.title && (
                      <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {section.title}
                      </h2>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {section.items.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 hover:shadow-lg transition-shadow">
                          {item.icon && (
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                              <span className="text-2xl">{item.icon}</span>
                            </div>
                          )}
                          <h3 className="font-bold text-lg text-slate-900 mb-2">{item.title}</h3>
                          <p className="text-slate-600 text-sm">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {section.type === 'cta' && (
                  <div className="px-4 max-w-5xl mx-auto my-8">
                    {/* If there's a title or description, show full CTA with background */}
                    {(section.title || section.description) ? (
                      <div 
                        style={{ 
                          background: section.bg_color || 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                        }}
                        className="rounded-xl text-center shadow-lg overflow-hidden p-8 md:p-12"
                      >
                        {section.title && (
                          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif', color: section.text_color || '#ffffff' }}>
                            {section.title}
                          </h2>
                        )}
                        {section.description && (
                          <p className="text-base md:text-lg mb-6 max-w-2xl mx-auto opacity-90" style={{ color: section.text_color || '#ffffff' }}>
                            {section.description}
                          </p>
                        )}
                        {section.button_text && (
                          <button 
                            onClick={() => setIsContactModalOpen(true)}
                            className={`inline-flex items-center px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 ${
                              section.button_style === 'outline' 
                                ? 'bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600'
                                : 'bg-white text-blue-600 hover:bg-blue-50 shadow-lg'
                            } ${
                              section.button_animation && section.button_animation !== 'none' 
                                ? `animate-${section.button_animation}` 
                                : ''
                            }`}
                          >
                            {section.button_text}
                            <ArrowRight size={18} className="ml-2" />
                          </button>
                        )}
                      </div>
                    ) : (
                      /* Button-only CTA - minimal styling, no background tile */
                      <div className="text-center">
                        {section.button_text && (
                          <button 
                            onClick={() => setIsContactModalOpen(true)}
                            style={{
                              background: section.bg_color || 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                            }}
                            className={`inline-flex items-center px-8 py-4 rounded-lg font-semibold text-white transition-all transform hover:scale-105 shadow-lg hover:shadow-xl ${
                              section.button_animation && section.button_animation !== 'none' 
                                ? `animate-${section.button_animation}` 
                                : ''
                            }`}
                          >
                            {section.button_text}
                            <ArrowRight size={18} className="ml-2" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Link/Button Section */}
                {section.type === 'link_button' && (
                  <div 
                    className="px-4 max-w-5xl mx-auto my-8 text-center"
                    style={{ backgroundColor: section.bg_color || 'transparent' }}
                  >
                    {section.title && (
                      <h3 className="text-xl font-semibold text-slate-800 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {section.title}
                      </h3>
                    )}
                    {section.link_style === 'button' && (
                      <a 
                        href={section.link_url || '#'}
                        target={section.open_new_tab ? '_blank' : '_self'}
                        rel={section.open_new_tab ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center px-8 py-3 text-white rounded-lg font-semibold transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                        style={{ backgroundColor: section.button_color || '#3b82f6' }}
                      >
                        {section.link_text || 'Click Here'}
                        <ArrowRight size={18} className="ml-2" />
                      </a>
                    )}
                    {section.link_style === 'link' && (
                      <a 
                        href={section.link_url || '#'}
                        target={section.open_new_tab ? '_blank' : '_self'}
                        rel={section.open_new_tab ? 'noopener noreferrer' : undefined}
                        className="text-blue-600 underline hover:text-blue-800 font-medium text-lg inline-flex items-center"
                      >
                        {section.link_text || 'Click Here'}
                        <ArrowRight size={16} className="ml-1" />
                      </a>
                    )}
                    {section.link_style === 'outline' && (
                      <a 
                        href={section.link_url || '#'}
                        target={section.open_new_tab ? '_blank' : '_self'}
                        rel={section.open_new_tab ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center px-8 py-3 rounded-lg font-semibold border-2 transition-all transform hover:scale-105"
                        style={{ borderColor: section.button_color || '#3b82f6', color: section.button_color || '#3b82f6' }}
                      >
                        {section.link_text || 'Click Here'}
                        <ArrowRight size={18} className="ml-2" />
                      </a>
                    )}
                  </div>
                )}

                {section.type === 'faq' && section.items && (
                  <div>
                    {section.title && (
                      <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {section.title}
                      </h2>
                    )}
                    <div className="space-y-4 max-w-3xl mx-auto">
                      {section.items.map((item, idx) => (
                        <details key={idx} className="bg-white rounded-lg border border-slate-200 p-4 group">
                          <summary className="font-semibold text-slate-900 cursor-pointer list-none flex justify-between items-center">
                            {item.question}
                            <span className="text-blue-600 group-open:rotate-180 transition-transform">▼</span>
                          </summary>
                          <p className="mt-3 text-slate-600">{item.answer}</p>
                        </details>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features Section */}
                {section.type === 'features' && section.items && (
                  <div style={{ backgroundColor: section.bg_color || '#f8fafc' }} className="py-16">
                    <div className="max-w-7xl mx-auto px-4">
                      {section.title && (
                        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {section.title}
                        </h2>
                      )}
                      <div className={`grid grid-cols-1 md:grid-cols-${section.columns || 3} gap-8`}>
                        {section.items.map((item, idx) => (
                          <div key={idx} className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow">
                            {item.icon && (
                              <div className="text-4xl mb-4">{item.icon}</div>
                            )}
                            <h3 className="font-bold text-xl text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              {item.title}
                            </h3>
                            <p className="text-slate-600">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats Section */}
                {section.type === 'stats' && section.items && (
                  <div 
                    style={{ 
                      backgroundColor: section.bg_color || '#1e40af',
                      color: section.text_color || '#ffffff'
                    }} 
                    className="py-16"
                  >
                    <div className="max-w-7xl mx-auto px-4">
                      {section.title && (
                        <h2 className="text-3xl font-bold mb-12 text-center" style={{ fontFamily: 'Manrope, sans-serif', color: section.text_color || '#ffffff' }}>
                          {section.title}
                        </h2>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {section.items.map((item, idx) => (
                          <div key={idx} className="text-center">
                            <div className="text-5xl font-bold mb-2" style={{ color: section.text_color || '#ffffff' }}>
                              {item.number}
                            </div>
                            <div className="text-lg opacity-90" style={{ color: section.text_color || '#ffffff' }}>
                              {item.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Video Section */}
                {section.type === 'video' && section.video_url && (
                  <div style={{ backgroundColor: section.bg_color || '#000000' }} className="py-16">
                    <div className="max-w-4xl mx-auto px-4">
                      {section.title && (
                        <h2 className="text-3xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {section.title}
                        </h2>
                      )}
                      <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
                        {section.video_type === 'file' ? (
                          <video 
                            controls 
                            className="w-full h-full"
                            style={{ objectFit: 'cover' }}
                          >
                            <source src={section.video_url} type="video/mp4" />
                            <source src={section.video_url} type="video/webm" />
                            <source src={section.video_url} type="video/ogg" />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <iframe
                            width="100%"
                            height="100%"
                            src={section.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                            title="Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          ></iframe>
                        )}
                      </div>
                      {section.description && (
                        <p className="text-center text-slate-300 mt-6">{section.description}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Testimonial Section */}
                {section.type === 'testimonial' && section.message && (
                  <div style={{ backgroundColor: section.bg_color || '#fef3c7' }} className="py-16">
                    <div className="max-w-4xl mx-auto px-4">
                      <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 relative">
                        <div className="absolute top-4 left-4 text-6xl text-slate-200 font-serif">"</div>
                        <div className="relative z-10">
                          <div className="flex items-center mb-6">
                            {[...Array(section.rating || 5)].map((_, i) => (
                              <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
                            ))}
                          </div>
                          <p className="text-lg text-slate-700 mb-6 italic leading-relaxed">
                            {section.message}
                          </p>
                          <div className="flex items-center gap-4">
                            {section.image_url && (
                              <img
                                src={section.image_url}
                                alt={section.name}
                                className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div>
                              <div className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {section.name}
                              </div>
                              {section.role && (
                                <div className="text-sm text-slate-600">
                                  {section.role}{section.company && ` at ${section.company}`}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Button Section */}
                {section.type === 'contact_form' && (
                  <div style={{ backgroundColor: section.bg_color || '#ffffff' }} className="py-16">
                    <div className="max-w-3xl mx-auto px-4 text-center">
                      {section.title && (
                        <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {section.title}
                        </h2>
                      )}
                      {section.subtitle && (
                        <p className="text-slate-600 mb-8">{section.subtitle}</p>
                      )}
                      
                      <button
                        onClick={() => {
                          setCurrentButtonConfig(section);
                          setIsContactModalOpen(true);
                        }}
                        style={{ backgroundColor: section.button_color || '#3b82f6' }}
                        className="inline-flex items-center px-8 py-4 rounded-lg text-white font-semibold text-lg hover:opacity-90 transition-all shadow-lg transform hover:scale-105"
                      >
                        {section.button_text || 'Contact Us'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!page.sections || page.sections.length === 0) && (
        <div className="py-16">
          <div className="max-w-3xl mx-auto px-4 text-center text-slate-500">
            <p>Content for this page is coming soon. Check back later!</p>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
        buttonConfig={currentButtonConfig}
      />
    </div>
  );
}
